import type {
  CompanySubscriptionResponse, EffectiveSubscriptionState, FeatureSummary, PlanSummary, RequiredSubscriptionAction, SubscriptionAccessMode,
} from './types';
import type { PlanChangeFeatureImpact } from './billingTypes';

const PLANS = {
  CorePayroll: { name: 'Core Payroll', tier: 1, base: 4000, perEmployee: 550 },
  PlusPayroll: { name: 'Plus Payroll', tier: 2, base: 7000, perEmployee: 750 },
  ProPlusPayroll: { name: 'Pro Plus Payroll', tier: 3, base: 11500, perEmployee: 950 },
} as const;

export type FixturePlanCode = keyof typeof PLANS;

/** The plans exactly as `GET /api/subscription-plans` returns them (integer cents, USD, monthly only, no employee limit). The mock gateway
 * and unit tests share this so mock mode shows the same prices and descriptions as the real catalog seed (subscription-catalog.json). */
export const FIXTURE_PLANS: PlanSummary[] = [
  { id: 'mock-core', code: 'CorePayroll', name: 'Core Payroll', description: 'Reliable payroll for businesses with work locations in one state.', tier: 1, baseMonthlyFeeCents: 4000, perEmployeeMonthlyFeeCents: 550 },
  { id: 'mock-plus', code: 'PlusPayroll', name: 'Plus Payroll', description: 'Everything in Core Payroll, plus work locations in more than one state, custom pay runs, timesheet approval and an accounting export.', tier: 2, baseMonthlyFeeCents: 7000, perEmployeeMonthlyFeeCents: 750 },
  { id: 'mock-proplus', code: 'ProPlusPayroll', name: 'Pro Plus Payroll', description: 'Everything in Plus Payroll, plus market pay benchmarks and advanced capabilities as they are released.', tier: 3, baseMonthlyFeeCents: 11500, perEmployeeMonthlyFeeCents: 950 },
];

const PLAN_NAME_BY_TIER: Record<number, string> = { 1: 'Core Payroll', 2: 'Plus Payroll', 3: 'Pro Plus Payroll' };

type FeatureRow = [
  featureKey: string, displayName: string, category: string, readinessStatus: string, tier: number,
  extras?: Partial<Pick<FeatureSummary, 'description' | 'upgradeMessage' | 'implementationNote'>>,
];

function feature([featureKey, displayName, category, readinessStatus, tier, extras]: FeatureRow): FeatureSummary {
  return {
    featureKey, displayName, category, readinessStatus, minimumPlanTier: tier, minimumPlanName: PLAN_NAME_BY_TIER[tier] ?? null,
    description: extras?.description ?? '',
    upgradeMessage: extras?.upgradeMessage ?? null, documentationPath: null,
    implementationNote: extras?.implementationNote ?? null, usageLimit: null, usageLimitUnit: null,
  };
}

/** The feature catalog exactly as `GET /api/subscription-features` returns it after the entitlements refresh: Beta and Operational features
 * are usable; the NotImplemented ones are "Coming soon" on every plan; the "Platform Examples" rows are internal demos that no customer-facing
 * comparison may show. Synthetic descriptions only. */
export const FIXTURE_FEATURES: FeatureSummary[] = ([
  ['single-state-payroll', 'Single-state payroll', 'Payroll', 'Beta', 1, {
    description: 'Run payroll for employees and work locations in one state. Extra work locations in that same state are included.',
    implementationNote: 'Tax calculation uses the sandbox tax provider until production tax verification is complete.',
  }],
  ['multi-state-payroll', 'Multi-state payroll', 'Payroll', 'Beta', 2, {
    description: 'Activate work locations in more than one state, each with its own verified tax setup.',
    upgradeMessage: 'Your plan covers work locations in one state. Plus supports work locations in more than one state.',
    implementationNote: 'Withholding is calculated only for jurisdictions the tax provider supports. The tax provider is a sandbox until production verification is complete.',
  }],
  ['custom-payroll-run', 'Custom Payroll Run', 'Payroll', 'Operational', 2, {
    description: 'Custom and off-cycle payroll runs (bonus, commission, correction, one-time pay).',
  }],
  ['auto-payroll', 'Auto Payroll', 'Payroll', 'NotImplemented', 1, { description: 'Automatic, recurring payroll runs with no manual trigger required.' }],
  ['expert-payroll-review', 'Expert Payroll Review', 'Payroll', 'NotImplemented', 2, { description: 'A human specialist reviews each payroll run before it is approved.' }],
  ['multiple-pay-rates', 'Multiple pay rates', 'Payroll', 'NotImplemented', 2, { description: 'Pay one employee at more than one rate, such as by role or by shift.' }],
  ['healthcare-compensation', 'Healthcare and per-visit compensation', 'Payroll', 'NotImplemented', 2, {
    description: 'Pay by the visit, shift, day or unit, with rates by discipline and service type.', implementationNote: 'Not available until it is released.',
  }],
  ['shift-differentials', 'Shift differentials', 'Payroll', 'NotImplemented', 2, { description: 'Add a differential for evening, night, weekend or holiday shifts.' }],
  ['multi-level-approvals', 'Multi-Level Approvals', 'Payroll', 'NotImplemented', 3, { description: 'Multi-step payroll approval chains.' }],
  ['next-day-direct-deposit', 'Next-Day Direct Deposit', 'Direct Deposit', 'Operational', 1, {
    description: 'Next-day direct deposit. Simulated payments only in this build; live direct deposit is blocked until activation, verification and cutover.',
  }],
  ['same-day-direct-deposit', 'Same-Day Direct Deposit', 'Direct Deposit', 'NotImplemented', 3, { description: 'Same-day settlement of direct deposit.' }],
  ['manager-timesheet-approval', 'Manager Timesheet Review & Approval', 'Time Tracking', 'Operational', 2, {
    description: 'Manager review and approval of submitted time entries (approve, reject with reason, reopen).',
  }],
  ['advanced-time-and-leave-tracking', 'Advanced Time & Leave Tracking', 'Time Tracking', 'NotImplemented', 2, {}],
  ['mobile-time-tracking', 'Track Time on the Go', 'Time Tracking', 'NotImplemented', 3, { description: 'A dedicated mobile app for employee time entry.' }],
  ['mobile-time-and-project-tracking', 'Track Time and Projects on the Go', 'Time Tracking', 'NotImplemented', 3, {}],
  ['health-benefits', 'Health Benefits for Your Team', 'HR & Benefits', 'Operational', 1, { description: 'Benefit-election management for employee health coverage.' }],
  ['401k-plans', '401(k) Plans', 'HR & Benefits', 'Operational', 1, { description: 'Benefit-election management for employee retirement contributions.' }],
  ['workers-comp-administration', "Workers' Compensation Administration", 'HR & Benefits', 'NotImplemented', 3, { description: "Workers' compensation policy and rate administration." }],
  ['accounting-csv-export', 'Accounting export (CSV)', 'Accounting', 'Beta', 2, {
    description: 'Turn a finalized payroll into a balanced journal entry and download it as a CSV for your accounting software.',
    upgradeMessage: 'Plus includes a CSV accounting export of finalized payroll. Reversals and your export history stay available on every plan.',
    implementationNote: 'CSV download only. There is no live connection to any accounting product.',
  }],
  ['advanced-gl-accounting', 'Advanced accounting', 'Accounting', 'NotImplemented', 3, {
    description: 'Reconciliation and direct accounting integrations, after each integration is certified.',
  }],
  ['job-role-library', 'Job role library', 'People', 'Operational', 2, {
    description: 'Start a job role from an EPN template and keep it on record with its history.',
    upgradeMessage: 'Plus includes the job role library. You can still create your own roles on every plan.',
  }],
  ['job-role-benchmarks', 'Market pay benchmarks', 'People', 'Beta', 3, {
    description: "See market pay reference figures beside a role's pay band.",
    implementationNote: 'Figures come from the datasets loaded for your environment. Test figures are labelled as such.',
  }],
  ['advanced-reporting-integrations', 'Advanced Reporting & Integrations', 'Reporting', 'Operational', 2, {
    description: 'The connection catalog, connections, integration requests and sync status. Standard payroll reports remain available on every plan.',
  }],
  ['basic-custom-reporting', 'Basic Custom Reporting', 'Reporting', 'NotImplemented', 2, {}],
  ['advanced-custom-reporting-analytics', 'Advanced Custom Reporting & Analytics', 'Reporting', 'NotImplemented', 3, {}],
  ['1099-efile-and-pay', '1099 E-file and Pay', 'Tax Filings', 'NotImplemented', 1, { description: 'Contractor 1099 tax-form preparation and electronic filing.' }],
  ['tax-penalty-protection', 'Tax Penalty Protection', 'Tax Filings', 'NotImplemented', 3, {}],
  ['expert-product-support', 'Expert Product Support', 'Support', 'NotImplemented', 1, { description: 'Standard-hours human product support.' }],
  ['hr-support-center', 'HR Support Center', 'Support', 'NotImplemented', 2, { description: 'Self-service HR guidance and document library.' }],
  ['expert-implementation-setup', 'Expert Implementation and Payroll Setup', 'Support', 'NotImplemented', 1, {
    description: 'Guided, human-assisted onboarding. Planned for every plan; not available until it is released.',
  }],
  ['24-7-expert-product-support', '24/7 Expert Product Support', 'Support', 'NotImplemented', 3, { description: 'Round-the-clock human product support.' }],
  ['personal-hr-advisor', 'Personal HR Advisor', 'Support', 'NotImplemented', 3, { description: 'A named, dedicated human HR advisor.' }],
  ['compliance-review-audit-assist', 'Compliance Review & Audit Assist', 'Support', 'NotImplemented', 2, { description: 'Human compliance review and audit assistance.' }],
  ['enhanced-support', 'Enhanced support', 'Support', 'NotImplemented', 2, { description: 'Faster responses and guided help beyond standard support.' }],
  ['universal-import', 'Universal and guided imports', 'Data Import', 'NotImplemented', 2, { description: 'Bring in data from other systems with a guided, validated import.' }],
  ['partner-multi-company', 'Partner portal and multi-company', 'Partner', 'NotImplemented', 3, { description: 'Manage many client companies from one partner account.' }],
  ['custom-security-roles', 'Custom roles and permission templates', 'Security & Access', 'NotImplemented', 3, {
    description: 'Create your own roles and saved permission templates.',
  }],
  ['api-access', 'API Access', 'Platform', 'NotImplemented', 3, {}],
  ['priority-report-access', 'Priority Report Access (illustrative example)', 'Platform Examples', 'Operational', 2, {}],
  ['advanced-analytics-access', 'Advanced Analytics Access (illustrative example)', 'Platform Examples', 'Operational', 3, {}],
] satisfies FeatureRow[]).map(feature).sort((a, b) => (a.category < b.category ? -1 : a.category > b.category ? 1 : 0));

/** What the server's plan-change preview lists for a change from `fromTier` to `toTier`: the usable (Operational or Beta), customer-facing
 * features whose required plan sits between the two. A downgrade lists them as `ReadOnly`, an upgrade as `Gained`. */
export function planChangeImpactFor(fromTier: number, toTier: number): { featuresAffected: PlanChangeFeatureImpact[]; consequenceNote: string | null } {
  const downgrade = toTier < fromTier;
  const [low, high] = downgrade ? [toTier, fromTier] : [fromTier, toTier];
  const featuresAffected = FIXTURE_FEATURES
    .filter((f) => (f.readinessStatus === 'Operational' || f.readinessStatus === 'Beta')
      && f.category !== 'Platform Examples' && f.minimumPlanTier !== null && f.minimumPlanTier > low && f.minimumPlanTier <= high)
    .map((f): PlanChangeFeatureImpact => ({
      featureKey: f.featureKey, displayName: f.displayName, category: f.category, readinessStatus: f.readinessStatus,
      requiredPlanName: f.minimumPlanName ?? '', consequence: downgrade ? 'ReadOnly' : 'Gained',
    }));
  return {
    featuresAffected,
    consequenceNote: downgrade && featuresAffected.length > 0
      ? 'Nothing is deleted. Your history stays viewable and exportable, and new activity in these features is blocked until the plan returns.'
      : null,
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** What the server would report as `trialEndingThresholdDays`: 7, 3 or 1 once the trial is that close to its end. */
export function trialEndingThresholdFor(daysRemaining: number | null): number | null {
  if (daysRemaining === null) return null;
  if (daysRemaining <= 1) return 1;
  if (daysRemaining <= 3) return 3;
  if (daysRemaining <= 7) return 7;
  return null;
}

const MODE_AND_ACTION: Record<Exclude<EffectiveSubscriptionState, 'NoSubscription'>, [SubscriptionAccessMode, RequiredSubscriptionAction | null]> = {
  TrialNotStarted: ['SetupOnly', 'StartTrial'],
  TrialActive: ['Full', null],
  TrialExpired: ['ReadOnly', 'ChoosePlan'],
  Active: ['Full', null],
  PendingChange: ['Full', null],
  PastDueGrace: ['Full', 'UpdatePayment'],
  PastDueRestricted: ['ReadOnly', 'UpdatePayment'],
  Canceled: ['ReadOnly', 'Reactivate'],
};

/**
 * Builds a `CompanySubscriptionResponse` exactly as the backend would report it for a given server-decided
 * state. Shared by the mock gateway (local development / Playwright) and unit tests so the two can never
 * drift apart on what each state looks like: a not-started trial has NO dates, an active trial has dates and
 * a days-remaining that is at least 1, an expired trial keeps its dates but reports no days remaining, a payment
 * failure in grace keeps plan access (`accessMode` Full) while a restricted one is read-only.
 */
export function subscriptionFixture(
  state: Exclude<EffectiveSubscriptionState, 'NoSubscription'>,
  options: { planCode?: FixturePlanCode; daysIn?: number; now?: number; overrides?: Partial<CompanySubscriptionResponse> } = {},
): CompanySubscriptionResponse {
  const planCode = options.planCode ?? 'PlusPayroll';
  const plan = PLANS[planCode];
  const now = options.now ?? Date.now();

  let trialStartUtc: string | null = null;
  let trialEndUtc: string | null = null;
  let trialDaysRemaining: number | null = null;
  let status = 'Active';

  if (state === 'TrialNotStarted') {
    status = 'TrialNotStarted';
  } else if (state === 'TrialActive') {
    const daysIn = options.daysIn ?? 6;
    status = 'Trialing';
    trialStartUtc = new Date(now - daysIn * DAY_MS).toISOString();
    trialEndUtc = new Date(now + (30 - daysIn) * DAY_MS).toISOString();
    trialDaysRemaining = Math.max(1, Math.ceil((30 - daysIn)));
  } else if (state === 'TrialExpired') {
    status = 'Trialing'; // the persisted status of an expired trial row — only the SERVER knows it is over
    trialStartUtc = new Date(now - 31 * DAY_MS).toISOString();
    trialEndUtc = new Date(now - DAY_MS).toISOString();
  } else if (state === 'Canceled') {
    status = 'Canceled';
  } else if (state === 'PendingChange') {
    status = 'PendingChange';
  } else if (state === 'PastDueGrace' || state === 'PastDueRestricted') {
    status = 'PastDue';
  }

  const paid = state === 'Active' || state === 'PendingChange' || state === 'PastDueGrace' || state === 'PastDueRestricted' || state === 'Canceled';
  const [accessMode, requiredAction] = MODE_AND_ACTION[state];

  return {
    id: 'sub-fixture', companyId: 'company-fixture', planCode, planName: plan.name, planTier: plan.tier,
    status, billingOwnership: 'ClientOwned', baseMonthlyFeeCents: plan.base, perEmployeeMonthlyFeeCents: plan.perEmployee,
    isTrialing: state === 'TrialActive', trialStartUtc, trialEndUtc, trialDaysRemaining,
    createdAtUtc: new Date(now - 31 * DAY_MS).toISOString(), updatedAtUtc: null,
    effectiveState: state,
    grantsPlanEntitlement: state === 'TrialActive' || state === 'Active' || state === 'PendingChange' || state === 'PastDueGrace',
    trialLengthDays: 30,
    trialStartRequirement: null,
    accessMode,
    requiredAction,
    trialEndingThresholdDays: trialEndingThresholdFor(trialDaysRemaining),
    paymentFailed: state === 'PastDueGrace' || state === 'PastDueRestricted',
    graceEndsUtc: state === 'PastDueGrace' ? new Date(now + 5 * DAY_MS).toISOString()
      : state === 'PastDueRestricted' ? new Date(now - DAY_MS).toISOString() : null,
    cancelAtPeriodEnd: false,
    currentPeriodEndUtc: paid ? new Date(now + (state === 'Canceled' ? -DAY_MS : 20 * DAY_MS)).toISOString() : null,
    pendingPlanCode: state === 'PendingChange' ? 'CorePayroll' : null,
    pendingPlanEffectiveUtc: state === 'PendingChange' ? new Date(now + 20 * DAY_MS).toISOString() : null,
    paymentMethod: paid ? { type: 'Card', brand: 'Visa', last4: '4242' } : null,
    provider: { configured: true, mode: 'test' },
    ...options.overrides,
  };
}
